import { useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { FaExclamationTriangle } from "react-icons/fa";
import { useContextSelector } from "use-context-selector";
import { CreateUserData, UserSchemaTypes } from "../../../../../@types/User";
import { PageHeader } from "../../../../../components/Headers/PageHeader";
import { SelectInput } from "../../../../../components/SelectInput";
import { StoresContext } from "../../../../../contexts/StoresContext";
import { useRegisterUsers } from "../../../../../hooks/api/Users/useRegisterUsers";
import { PermissionsModal } from "../components/PermissionsModal";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ZodType, z } from "zod";
import { createUserSchema, editUserSchema } from "./schemas";

import { UsersPermissionsContext } from "../../../../../contexts/UsersPermissions";
import { wrapperRequests } from "../../../../../services/api";
import { routesURL } from "../../../../../services/routesUrl";
import * as S from "./styles";
import { Input } from "../../../../../components/New/Input";
import { SwitchInput } from "../../../../../components/Switch/SwitchInput";
import { Table } from "../../../../../components/New/Table";
import { ColumnDef } from "@tanstack/react-table";
import { SearchData, StoreListTypes } from "../../../../../@types/Stores";
import { SearchInput } from "../../../../../components/New/SearchInput";
import { Checkbox } from "../../../../../components/New/Checkbox";
import { Loading } from "../../../../../components/New/Loading";
import { Button } from "../../../../../components/New/Button";
import { Modal } from "../../../../../components/New/Modal";
import { useMediaQuery } from "usehooks-ts";
import { useTheme } from "styled-components";
import { DrawerComponent } from "../../../../../components/New/Drawer";
import { LanguageSelector } from "../../../../../components/LanguageSelector";

export function RegisterUser() {
  const { t } = useTranslation();
  const [userDataToEdit, setUserDataToEdit] = useState({} as UserSchemaTypes);
  const [loadingUser, setLoadingUser] = useState(false);
  const [userStoresGranted, setUserStoresGranted] = useState<string[]>([]);
  const [userDefaultStore, setUserDefaultStore] = useState("");
  const [userUsePassword, setUserUsePassword] = useState(false);
  const [registerFormSchema, setRegisterFormSchema] = useState(
    {} as ZodType<any, any, any>,
  );
  const [searchStore, setSearchStore] = useState<SearchData>({
    shopName: null,
    type: null,
    tenantName: null,
    page: 1,
  });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const theme = useTheme();
  const isResponsive = useMediaQuery(
    `(max-width: ${theme.responsiveDesign.sm})`,
  );

  type RegisterFormData = z.infer<typeof registerFormSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CreateUserData>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: userDataToEdit,
  });

  const {
    allStoresAllTenants,
    allStoresSelectPage,
    allStoresSelectRowsPerPage,
    fetchAllStoresAllTenants,
    storesLoading,
  } = useContextSelector(StoresContext, (context) => {
    return context;
  });

  const { createUser, registerUserLoading, registerUserError, updateUser } =
    useRegisterUsers();

  const {
    getAllRoles,
    allRoles,
    handleAddAcessNewRole,
    handleRemoveAcessRole,
    userRoles,
    getUserPermissions,
    handleDefaultUserRoles,
  } = useContextSelector(UsersPermissionsContext, (context) => {
    return context;
  });

  const navigate = useNavigate();
  const { userId } = useParams();

  const isEditUser = userId;

  async function handleAddUser(data: RegisterFormData) {
    await createUser(data);
  }

  async function handleEditUser(data: RegisterFormData) {
    isEditUser && (await updateUser({ data, isEditUser }));
  }

  async function handleSearchStore() {
    await fetchAllStoresAllTenants(searchStore);
  }

  function handleCheckBoxChange(code: string) {
    const updatedStoresGranted = userStoresGranted.includes(code)
      ? userStoresGranted.filter((item) => item !== code)
      : [...userStoresGranted, code];

    const defaultStoreWithStoreAccess =
      updatedStoresGranted.includes(userDefaultStore);

    if (!defaultStoreWithStoreAccess && userStoresGranted.length === 0) {
      setUserDefaultStore(code);
    } else if (!defaultStoreWithStoreAccess) {
      setUserDefaultStore("");
    }

    setUserStoresGranted(updatedStoresGranted);
  }

  function handleSelectDefaultShop(code: string) {
    if (!userStoresGranted.includes(code)) {
      setUserStoresGranted([...userStoresGranted, code]);
      return setUserDefaultStore(code);
    }

    return setUserDefaultStore(code);
  }

  const storesColumns: ColumnDef<StoreListTypes>[] = [
    {
      accessorKey: "name",
      header: () => (
        <S.StoreNameContainer>
          <span>{t("storeName")}</span>
        </S.StoreNameContainer>
      ),
      size: 300,
      cell: (props) => (
        <S.StoreNameContainer>
          <Checkbox
            checked={userStoresGranted.includes(props.row.original.id)}
            onClick={() => handleCheckBoxChange(props.row.original.id)}
          />
          <span>{props.getValue()}</span>
        </S.StoreNameContainer>
      ),
    },
    {
      accessorKey: "code",
      header: t("storeType"),
      size: 100,
      cell: (props) => (
        <S.StoreTypeContainer>
          <span>{t("warehouse")}</span>
        </S.StoreTypeContainer>
      ),
    },
    {
      accessorKey: "active",
      header: t("status"),
      size: 90,
      cell: (props) => (
        <S.StatusContainer
          variant={props.row.original.active ? "Active" : "Inactive"}
        >
          <span>{props.row.original.active ? t("active") : t("inactive")}</span>
        </S.StatusContainer>
      ),
    },
    {
      accessorKey: "tenantName",
      header: t("region"),
      size: 130,
      cell: (props) => <span>{props.getValue()}</span>,
    },
    {
      accessorKey: "state",
      header: t("state"),
      size: 100,
      cell: (props) => <span>{props.getValue()}</span>,
    },
    {
      accessorKey: "city",
      header: t("city"),
      size: 100,
      cell: (props) => <span>{props.getValue()}</span>,
    },
    {
      accessorKey: "id",
      header: t("defaultStore"),
      size: 150,
      cell: (props) => (
        <S.DefaultStoreContainer>
          <Checkbox
            checked={userDefaultStore === props.row.original.id}
            onClick={() => handleSelectDefaultShop(props.row.original.id)}
            style={{ alignSelf: "center" }}
          />
        </S.DefaultStoreContainer>
      ),
    },
  ];

  useEffect(() => {
    fetchAllStoresAllTenants();
  }, [fetchAllStoresAllTenants]);

  useEffect(() => {
    const loadUserToEdit = async () => {
      if (isEditUser) {
        setLoadingUser(true);
        try {
          const response = await wrapperRequests(
            routesURL.settings.users.getUser(isEditUser),
            "GET",
          );

          setUserDataToEdit(response.data);

          const storeId = response.data.shopsGranted.map(({ id }: any) => {
            return id;
          });
          setUserStoresGranted(storeId);
          setUserDefaultStore(response.data?.defaultShop?.id || "");
          setUserUsePassword(response.data?.usePassword ?? false);

          setValue("name", response.data.name);
          setValue("email", response.data.email);
          setValue("defaultShop", response.data?.defaultShop?.id || "");
          setValue("shopsGranted", storeId);
          setValue("usePassword", response.data?.usePassword ?? false);
          setValue("password", "");
          setValue("passwordConfirmation", "");
          setValue("language", response.data?.language || "");
        } catch (error) {
          toast.error(t("userDoesNotExist"), {
            position: "top-center",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
          navigate("/settings/users");
        } finally {
          setLoadingUser(false);
        }
      } else {
        setValue("defaultShop", "");
        setValue("shopsGranted", []);
        setValue("usePassword", false);
        setValue("password", "");
        setValue("passwordConfirmation", "");
        setValue("language", "");
      }
    };
    loadUserToEdit();
  }, [isEditUser, setValue, navigate]);

  useEffect(() => {
    if (isEditUser) {
      return setRegisterFormSchema(
        editUserSchema({
          userDefaultStore,
          userStoresGranted,
          userUsePassword,
        }),
      );
    }
    setRegisterFormSchema(
      createUserSchema({
        userDefaultStore,
        userStoresGranted,
        userUsePassword,
      }),
    );
  }, [isEditUser, userDefaultStore, userStoresGranted, userUsePassword]);

  useEffect(() => {
    getAllRoles();

    if (isEditUser) {
      getUserPermissions(isEditUser);
    }
  }, []);

  useEffect(() => {
    if (allRoles.length && userDataToEdit.roles?.length > 0) {
      const defaultUserRoles = allRoles.filter((role) =>
        userDataToEdit.roles?.find((_role) => _role.id === role.id),
      );

      handleDefaultUserRoles(defaultUserRoles);
    }
  }, [allRoles, userDataToEdit.roles]);

  return (
    <S.Container>
      <PageHeader
        title={isEditUser ? t("editUser") : t("createNewUser")}
        description={
          isEditUser
            ? t("editUserInformationAndAccess")
            : t("createNewUserToAccess")
        }
        customBreadcrumbString={
          isEditUser
            ? loadingUser
              ? t("loading")
              : userDataToEdit.name || t("loading")
            : t("createNewUser")
        }
      />

      <S.Form
        autoComplete="off"
        onSubmit={handleSubmit(isEditUser ? handleEditUser : handleAddUser)}
        isEditUser={!!isEditUser}
      >
        <S.UsersTitle>{t("userInformation")}</S.UsersTitle>
        <S.InputRowContainer isFirst>
          <Input
            title={t("name")}
            placeholder={t("enterName")}
            errorMessage={errors.name?.message}
            {...register("name")}
            width={isResponsive ? "100%" : "20rem"}
          />

          {!isResponsive && (
            <SwitchInput
              title={t("remoteAccess")}
              label={userUsePassword ? t("on") : t("off")}
              checked={!!userUsePassword}
              onChange={() => setUserUsePassword(!userUsePassword)}
              width="20rem"
            />
          )}
        </S.InputRowContainer>

        <S.InputRowContainer>
          <Input
            title={t("email")}
            placeholder={t("enterEmail")}
            disabled={!!isEditUser}
            errorMessage={errors?.email?.message}
            {...register("email")}
            width={isResponsive ? "100%" : "20rem"}
          />

          {isResponsive && (
            <SwitchInput
              title={t("remoteAccess")}
              label={userUsePassword ? t("on") : t("off")}
              checked={!!userUsePassword}
              onChange={() => setUserUsePassword(!userUsePassword)}
              width="100%"
            />
          )}

          <Input
            title={t("pincode")}
            placeholder={t("enterPincode")}
            errorMessage={errors?.pin?.message}
            {...register("pin")}
            width={isResponsive ? "100%" : "20rem"}
          />
        </S.InputRowContainer>

        {userUsePassword && (
          <S.InputRowContainer>
            <Input
              title={t("password")}
              type="password"
              placeholder={t("enterPassword")}
              errorMessage={errors?.password?.message}
              {...register("password")}
              width={isResponsive ? "100%" : "20rem"}
            />

            <Input
              title={t("confirmPassword")}
              type="password"
              placeholder={t("enterPassword")}
              errorMessage={errors?.passwordConfirmation?.message}
              {...register("passwordConfirmation")}
              width={isResponsive ? "100%" : "20rem"}
            />
          </S.InputRowContainer>
        )}

        {errors.defaultShop?.message ||
        errors.shopsGranted?.message ||
        registerUserError ? (
          <S.ErrorContainer>
            <FaExclamationTriangle />
            <label>
              {(registerUserError && registerUserError) ||
                (errors.shopsGranted?.message &&
                  errors.shopsGranted?.message) ||
                (errors.defaultShop?.message && errors.defaultShop?.message)}
            </label>
          </S.ErrorContainer>
        ) : null}

        {isResponsive && isEditUser && <S.Separator />}

        {isEditUser && (
          <S.PermissionsContainer>
            <S.RolesAndLanguagesContainer>
              <div>
                <S.AlignTitle>
                  <S.UsersTitle>{t("rolesAndPermissions")}</S.UsersTitle>
                  <S.UsersTitleDescription>
                    {t("rolesAndPermissionsDescription")}
                  </S.UsersTitleDescription>
                </S.AlignTitle>

                <S.RolesAcess>
                  <SelectInput
                    title={t("roles")}
                    variant="resized"
                    onChange={(event) => {
                      handleAddAcessNewRole(
                        allRoles.find(
                          (role) => role.name === event.target.value,
                        ),
                      );
                    }}
                    placeholder={t("selectAnOption")}
                    options={allRoles.map((role) => ({
                      id: role.id,
                      value: role.name,
                      label: role.name,
                    }))}
                    width={isResponsive ? "100%" : "20rem"}
                  />

                  {!!userRoles.length && (
                    <S.AccessGrantedContainer>
                      <S.UsersTitle>{t("accessGranted")}</S.UsersTitle>
                      <ul>
                        {userRoles.map((role) => (
                          <li key={role.id} title={role.name}>
                            <span>{role.name}</span>

                            <IoMdClose
                              style={{ cursor: "pointer" }}
                              size={16}
                              onClick={() => {
                                handleRemoveAcessRole(role);
                              }}
                            />
                          </li>
                        ))}
                      </ul>
                    </S.AccessGrantedContainer>
                  )}
                </S.RolesAcess>
              </div>
            </S.RolesAndLanguagesContainer>
            {!isResponsive && (
              <Modal
                customButtons
                modalContent={<PermissionsModal userId={userId} />}
                title={t("editPermissions")}
              >
                <Button
                  title={t("editPermissions")}
                  variant="ghost-gray"
                  width="9.1rem"
                  style={{ minWidth: 146 }}
                />
              </Modal>
            )}

            {isResponsive && (
              <>
                <Button
                  title={t("editPermissions")}
                  variant="ghost-gray"
                  width="100%"
                  height="2.5rem"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterDrawerOpen(true);
                  }}
                />

                <DrawerComponent
                  open={filterDrawerOpen}
                  onOpenChange={setFilterDrawerOpen}
                  variant="filter"
                  description=""
                  triggerContent={null}
                  width="100%"
                  title={t("editPermissions")}
                  showCloseButton
                >
                  <PermissionsModal
                    userId={userId}
                    isResponsive={isResponsive}
                    setCloseDrawer={() => setFilterDrawerOpen(false)}
                  />
                </DrawerComponent>
              </>
            )}
          </S.PermissionsContainer>
        )}

        <S.LanguagesContainer>
          <LanguageSelector
            register={register("language")}
            defaultValue={userDataToEdit?.language || ""}
            width={isResponsive ? "100%" : "20rem"}
          />
        </S.LanguagesContainer>
      </S.Form>

      {isResponsive && <S.Separator style={{ marginBottom: "1.5rem" }} />}

      <S.AlignTitle style={{ marginBottom: isResponsive ? "1rem" : "0.75rem" }}>
        <S.UsersTitle>{t("stores")}</S.UsersTitle>
        <S.UsersTitleDescription>
          {t("selectStoresDescription")}
        </S.UsersTitleDescription>
      </S.AlignTitle>

      <SearchInput
        onPressEnter={() => handleSearchStore()}
        onChange={(e) =>
          setSearchStore((currentState) => {
            return {
              ...currentState,
              shopName: e.target.value,
            };
          })
        }
      />

      {storesLoading && <Loading />}

      {!!allStoresAllTenants.content.length && !storesLoading && (
        <Table
          columns={storesColumns}
          columnToFilter=""
          data={allStoresAllTenants.content}
          key={
            allStoresAllTenants.content.length ||
            userStoresGranted.length ||
            userDefaultStore
          }
          style={{
            marginTop: isResponsive ? "1rem" : "0.75rem",
            width: "100%",
          }}
          pagination={{
            page: allStoresAllTenants.page,
            totalPages: allStoresAllTenants.totalPages,
            rowsPerPage: allStoresAllTenants.rowsPerPage,
            selectPage: allStoresSelectPage,
            selectRowsPerPage: allStoresSelectRowsPerPage,
          }}
        />
      )}

      <S.ButtonsContainer>
        <Button
          title={t("save")}
          isLoading={registerUserLoading}
          onClick={() =>
            handleSubmit(isEditUser ? handleEditUser : handleAddUser)()
          }
        />
        <Button
          isLoading={registerUserLoading}
          title={t("cancel")}
          variant="ghost"
          onClick={() => navigate("/settings/users")}
        />
      </S.ButtonsContainer>
    </S.Container>
  );
}
